import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";

import {
  resolveAccountIdFromLog,
  resolveTenantIdFromLog,
} from "../../../../helpers/activity_log_links";
import { ActivityLog } from "../../../../services/superAdmin/adminActivityLogService";

interface ActivityLogDetailModalProps {
  isOpen: boolean;
  log: ActivityLog | null;
  onClose: () => void;
  formatDateTime: (value: string) => string;
  formatActionLabel: (action: string) => string;
}

const formatJsonBlock = (value: Record<string, unknown> | null): string => {
  if (value === null || Object.keys(value).length === 0) {
    return "";
  }

  return JSON.stringify(value, null, 2);
};

const ActivityLogDetailModal = ({
  isOpen,
  log,
  onClose,
  formatDateTime,
  formatActionLabel,
}: ActivityLogDetailModalProps) => {
  const { t } = useTranslation();

  const oldValuesJson = useMemo(
    () => (log ? formatJsonBlock(log.old_values) : ""),
    [log]
  );

  const newValuesJson = useMemo(
    () => (log ? formatJsonBlock(log.new_values) : ""),
    [log]
  );

  const notAvailable = t("admin.activityLogs.common.notAvailable");
  const tenantId = log ? resolveTenantIdFromLog(log) : null;
  const accountId = log ? resolveAccountIdFromLog(log) : null;

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        {t("admin.activityLogs.details.title")}
      </ModalHeader>
      <ModalBody>
        {!log ? (
          <p className="text-muted mb-0">{notAvailable}</p>
        ) : (
          <>
            <Row className="g-3 mb-4">
              <Col md={6}>
                <p className="text-muted mb-1 fs-12">
                  {t("admin.activityLogs.details.date")}
                </p>
                <p className="fw-medium mb-0">{formatDateTime(log.created_at)}</p>
              </Col>
              <Col md={6}>
                <p className="text-muted mb-1 fs-12">
                  {t("admin.activityLogs.details.action")}
                </p>
                <Badge
                  color="soft-primary"
                  className="fs-12"
                  title={t("admin.activityLogs.actionTooltip", {
                    code: log.action,
                  })}
                >
                  {formatActionLabel(log.action)}
                </Badge>
                <p className="text-muted fs-11 mb-0 mt-1 font-monospace">
                  {log.action}
                </p>
              </Col>
              <Col md={6}>
                <p className="text-muted mb-1 fs-12">
                  {t("admin.activityLogs.details.admin")}
                </p>
                <p className="mb-0">{log.super_admin?.name ?? notAvailable}</p>
              </Col>
              <Col md={6}>
                <p className="text-muted mb-1 fs-12">
                  {t("admin.activityLogs.details.user")}
                </p>
                <p className="mb-0">{log.user?.name ?? notAvailable}</p>
              </Col>
              <Col md={6}>
                <p className="text-muted mb-1 fs-12">
                  {t("admin.activityLogs.details.route")}
                </p>
                <p className="mb-0 font-monospace">{log.route}</p>
              </Col>
              <Col md={6}>
                <p className="text-muted mb-1 fs-12">
                  {t("admin.activityLogs.details.ip")}
                </p>
                <p className="mb-0 font-monospace">{log.ip_address}</p>
              </Col>
              {tenantId != null || accountId != null ? (
                <Col md={12} className="d-flex flex-wrap gap-2">
                  {tenantId != null ? (
                    <Button
                      tag={Link}
                      to={`/super-admin/tenants?tenant_id=${tenantId}`}
                      color="soft-secondary"
                      size="sm"
                      onClick={onClose}
                    >
                      <i className="ri-building-line align-bottom me-1" />
                      {t("admin.activityLogs.actions.openTenant")} #{tenantId}
                    </Button>
                  ) : null}
                  {accountId != null ? (
                    <Button
                      tag={Link}
                      to={`/super-admin/accounts?account_id=${accountId}`}
                      color="soft-info"
                      size="sm"
                      onClick={onClose}
                    >
                      <i className="ri-user-shared-line align-bottom me-1" />
                      {t("admin.activityLogs.actions.openAccount")} #{accountId}
                    </Button>
                  ) : null}
                </Col>
              ) : null}
            </Row>

            <div className="mb-3">
              <h6 className="mb-2">{t("admin.activityLogs.details.oldValues")}</h6>
              {oldValuesJson ? (
                <pre className="bg-light border rounded p-3 mb-0 font-monospace fs-12 activity-log-json">
                  {oldValuesJson}
                </pre>
              ) : (
                <p className="text-muted mb-0">{t("admin.activityLogs.details.noPayload")}</p>
              )}
            </div>

            <div>
              <h6 className="mb-2">{t("admin.activityLogs.details.newValues")}</h6>
              {newValuesJson ? (
                <pre className="bg-light border rounded p-3 mb-0 font-monospace fs-12 activity-log-json">
                  {newValuesJson}
                </pre>
              ) : (
                <p className="text-muted mb-0">{t("admin.activityLogs.details.noPayload")}</p>
              )}
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ActivityLogDetailModal;
